import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  TrophyIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { LeagueService, SeasonService, LeagueSeasonService, TeamService } from '../../services/firebaseService';
import SeasonTeamsList from '../../components/shared/SeasonTeamsList';

export default function LeagueStandings() {
  const { leagueId, seasonId } = useParams();
  const [league, setLeague] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [teamsCount, setTeamsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId && seasonId) {
      loadLeagueData();
    }
  }, [leagueId, seasonId]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const leagueData = await LeagueService.getLeague(leagueId);
      if (!leagueData) {
        throw new Error('League not found');
      }
      
      setLeague(leagueData);
      
      // Load the specific league-season data
      const leagueSeasonData = await LeagueSeasonService.getLeagueSeason(leagueId, seasonId);
      if (!leagueSeasonData) {
        throw new Error('Season not found in this league');
      }
      
      // Load the full season data
      const seasonData = await SeasonService.getSeason(seasonId);
      if (!seasonData) {
        throw new Error('Season data not found');
      }
      
      // Combine league-season data with full season data
      setSelectedSeason({
        ...leagueSeasonData,
        ...seasonData
      });
      
      // Fetch teams count
      const teams = await TeamService.getLeagueSeasonTeams(leagueId, seasonId);
      setTeamsCount(teams?.length || 0);
      
    } catch (error) {
      console.error('Error loading league data:', error);
      setError(error.message || 'Failed to load league data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading standings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <TrophyIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Standings</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={loadLeagueData}
              className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
            >
              Try Again
            </button>
            <Link
              to={`/manage-leagues/${leagueId}`}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Back to League
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!league || !selectedSeason) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <TrophyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Season Selected</h2>
          <p className="text-gray-600 mb-4">Please select a season to view standings.</p>
          <Link
            to={`/manage-leagues/${leagueId}`}
            className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
          >
            Back to League
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link
              to={`/manage-leagues/${leagueId}`}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">League Standings</h1>
              <p className="mt-1 text-lg text-gray-600">{league.name} - Season {seasonId}</p>
            </div>
          </div>
        </div>


        {/* Teams Leaderboard */}
        <div className="mb-6">
          <SeasonTeamsList
            leagueId={leagueId}
            seasonId={seasonId}
            season={selectedSeason}
            league={league}
            showActions={false}
            showTeamLinks={true}
          />
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Teams
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {teamsCount}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Contestants
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {selectedSeason.contestants?.length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Weeks
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {selectedSeason.weeks?.length || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
